/**
 * Task 22 — plaintext purchase-history CSV (not an encrypted backup).
 * Columns: date, merchant, category, amount (cents), card suffix, expected reward cents, notes.
 */

import type { Cents } from '../../domain/money';
import type { SpendCategory } from '../../domain/recommend/types';

export const PURCHASE_HISTORY_CSV_HEADERS = [
  'date',
  'merchant_name',
  'merchant_category',
  'amount',
  'card_suffix',
  'expected_reward_cents',
  'notes',
] as const;

export type PurchaseHistoryCsvRow = {
  date: string;
  merchant_name: string;
  merchant_category: SpendCategory | null;
  /** Integer cents — no floats (ADR-003). */
  amount_cents: Cents;
  card_suffix: string;
  expected_reward_cents: Cents | null;
  notes: string | null;
};

/** RFC 4180 field escaping. */
export function escapeCsvField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function centsField(value: Cents | null): string {
  return value === null ? '' : value.toString();
}

export function buildPurchaseHistoryCsv(rows: readonly PurchaseHistoryCsvRow[]): string {
  const header = PURCHASE_HISTORY_CSV_HEADERS.join(',');
  const body = rows.map((row) =>
    [
      escapeCsvField(row.date),
      escapeCsvField(row.merchant_name),
      escapeCsvField(row.merchant_category ?? ''),
      centsField(row.amount_cents),
      escapeCsvField(row.card_suffix),
      centsField(row.expected_reward_cents),
      escapeCsvField(row.notes ?? ''),
    ].join(','),
  );
  return `${[header, ...body].join('\n')}\n`;
}

export const PURCHASE_HISTORY_CSV_FILENAME = 'cardzapp-purchase-history.csv';

/**
 * Trigger a browser download of the CSV string.
 * Separated so tests can assert the CSV without mocking download APIs.
 */
export function downloadPurchaseHistoryCsv(
  csv: string,
  filename: string = PURCHASE_HISTORY_CSV_FILENAME,
): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
